import { useMemo } from 'react';

import * as yup from 'yup';
import { useFormik } from 'formik';

import i18n from '../../../../../../../i18n';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import { useAppConfig } from '../../../../../../../hooks/use-app-config';
import { useShowTaxCalculationFields } from '../../../../../hooks/use-show-tax-calculation-fields';

interface DynamicFields {
  name: string;
  defaultValue?: any;
  isRequired: boolean;
  displayName: string;
  type: 'text' | 'list';
}

const getValidationSchema = (additionalFields: DynamicFields[]) => {
  const formValidation = additionalFields.reduce((fields, field) => {
    let fieldValidation = field.isRequired
      ? yup.string().required(
          i18n.t('checkout.fieldErrors.incomplete', {
            field: i18n.t(`checkout.${field.displayName}`),
          })
        )
      : yup.string().notRequired();
    if (field.name === 'email') {
      fieldValidation = yup
        .string()
        .required(i18n.t('checkout.fieldErrors.incompleteEmail'))
        .email(i18n.t('checkout.fieldErrors.invalidEmail'));
    }
    return {
      ...fields,
      [field.name]: fieldValidation,
    };
  }, {});
  const formSchema = yup.object().shape({ ...formValidation });
  return formSchema;
};

// const mandatoryUserFieldsDisplayNameMap: Record<string, string> = {
//   zip: 'Zip',
//   city: 'City',
//   phone: 'Phone',
//   email: 'Email',
//   address: 'Address',
//   lastName: 'Last name',
//   firstName: 'First name',
// };

const getMandatoryFields = (mandatoryUserFieldsObj: Record<string, boolean>, email: string) => {
  const fields = Object.keys(mandatoryUserFieldsObj).map((fieldName) => {
    return {
      name: fieldName,
      defaultValue: fieldName === 'email' ? email : '',
      isRequired: true,
      type: 'text',
      displayName: fieldName,
    };
  });
  return fields as DynamicFields[];
};

export function useDynamicForm() {
  const { selectedPaymentMethod, checkoutData } = useCheckout();
  const { featureFlags } = useAppConfig();
  const shouldHideEmailField = featureFlags?.checkout_enforce_customer_email;

  const email = checkoutData?.customer?.email;
  const { useTaxCalculationField } = useShowTaxCalculationFields();

  const { formFields, formInitialValues } = useMemo(() => {
    const mandatoryUserFields = selectedPaymentMethod?.mandatoryUserFields;
    const additionalInputFields = selectedPaymentMethod?.additionalInputFields;
    const mandatoryFields = mandatoryUserFields
      ? getMandatoryFields(mandatoryUserFields, email || '')
      : [];
    if (useTaxCalculationField) {
      const zipCodeFieldIndex = mandatoryFields.findIndex((field) => field.name === 'zip');
      if (zipCodeFieldIndex > -1) {
        mandatoryFields.splice(zipCodeFieldIndex, 1);
      }
    }
    if (shouldHideEmailField) {
      const emailFieldIndex = mandatoryFields.findIndex((field) => field.name === 'email');
      if (emailFieldIndex > -1) {
        mandatoryFields.splice(emailFieldIndex, 1);
      }
    }
    const additionalFields = additionalInputFields
      ? (additionalInputFields.map((field) => ({
          name: field.key,
          isRequired: true,
          type: field.type,
          displayName: field.key,
        })) as DynamicFields[])
      : [];
    const formFields = [...mandatoryFields, ...additionalFields];

    const formInitialValues = formFields.reduce(
      (fields, currentField) => ({
        ...fields,
        [currentField.name]: currentField.defaultValue || '',
        ...(shouldHideEmailField ? { email: email || '' } : {}),
      }),
      {}
    );
    return {
      formFields,
      formInitialValues,
    };
  }, [selectedPaymentMethod, useTaxCalculationField, email, shouldHideEmailField]);

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: getValidationSchema(formFields),
    initialValues: formInitialValues,
    onSubmit: (values) => {
      console.log({ values });
    },
  });

  return { formik, formFields };
}
